import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { IPage, IPoll, IQuestion } from '../../interfaces/poll';
import { IResponses } from '../../interfaces/poll-responses';
import { HeaderComponent } from '../header/header.component';
import { PollIncentiveComponent } from "./poll-incentive/poll-incentive.component";
import { IIncentive } from "../../interfaces/incentive";
import { INCENTIVE_DEFAULT } from "../../constants/incentive-default.const";
import { LibDrawBaseService } from "../../base/lib-draw-base.service";
import { PollTypeEnum } from "../../../../../sapore-survey-pwa/src/app/shared/enums/poll-type.enum";

/** Indice fora do range de paginas: encerra a pesquisa. */
const END_OF_POLL_PAGE_INDEX = 100;

/** Tempo sem interacao que encerra uma sessao ja iniciada, em paginas sem timer proprio. */
const INACTIVITY_TIMEOUT_SECONDS = 90;

@Component({
  selector: 'lib-poll-manager',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, HeaderComponent, PollIncentiveComponent],
  templateUrl: './poll-manager.component.html',
  styleUrls: ['./poll-manager.component.css'],
})
export class PollManagerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() poll!: IPoll;
  @Input() incentive: IIncentive | null = null;
  @Input() isLoadingDone: boolean = false;
  @Output() onFinishPoll = new EventEmitter<IResponses[]>();

  private readonly drawService = inject(LibDrawBaseService);

  protected readonly INCENTIVE_DEFAULT = INCENTIVE_DEFAULT;
  colorsWithWeight = [
    '',
    '#ed1818',
    '#FFCC00',
    '#A8CE1F',
    '#26BF26',
    '#ed1818',
    '#FFCC00',
    '#A8CE1F',
    '#26BF26',
  ];

  indexSelectedPage = 0;
  time = new BehaviorSubject<number>(0);
  intervalId!: any;

  page?: IPage;
  showBtn = false;
  disabled = true;

  responses: IResponses[] = [];

  private inactivityTimeoutId: ReturnType<typeof setTimeout> | null = null;

  nextPage: number | null = null;
  thumbsImgPath = 'assets/svg/survey-thumbs-up.svg';
  thumbsIncompleteImgPath = 'assets/svg/survey-thumbs-down.svg';
  logoImgPath = 'assets/img/logo.png';

  showInitialIncentive = false;
  showWinnerIncentive = false;

  isLoading = false;

  drawNumber: number = 0;

  pollIncomplete: boolean = false;

  get isReservation(): boolean {
    return this.poll.type === PollTypeEnum.Reservation;
  }

  get isSatisfaction(): boolean {
    return this.poll.type === PollTypeEnum.Satisfaction;
  }

  get textAreaPlaceholder(): string {
    return this.isReservation ? 'Digite aqui...' : 'Ex. Gostei muito...';
  }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['isLoadingDone']) {
      this.isLoading = changes['isLoadingDone'].currentValue;
    }

    if (changes['poll'] && this.poll) {
      this.startNewSession();
    }

    // Nao levanta a tela de incentivo por cima de quem esta respondendo: o toque que a
    // dispensa inicia uma nova sessao e descartaria a resposta em andamento. Ela reaparece
    // sozinha no fim do ciclo atual, pelo timeout da tela final (countTime).
    if (changes['incentive'] && this.incentive && !this.hasSessionStarted) {
      this.showInitialIncentive = true;
      this.showWinnerIncentive = false;
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.stopInactivityTimer();
  }

  startTimer(duration: number, onTimeout: () => void) {
    this.stopTimer();
    this.time.next(duration);
    this.intervalId = setInterval(() => {
      if (this.time.value === 1) {
        this.stopTimer();
        onTimeout();
      } else {
        this.time.next(this.time.value - 1);
      }
    }, 1000);
  }

  stopTimer() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  selectPage(index: number): void {
    this.nextPage = null;
    this.flushCurrentPage();

    const pageSelected = this.createPropToAnswer(this.poll.pages[index]);

    if (pageSelected?.questions.length > 1) {
      this.showBtn = true;
    } else if (pageSelected?.questions.some((q) => q.type !== 1)) {
      this.showBtn = true;
    } else {
      this.showBtn = !!(pageSelected?.pageNumber !== 1 &&
        pageSelected?.questions.some((q) => q.type === 1 && !q.requiredAnswer));
    }
    this.indexSelectedPage = index;

    this.page = pageSelected;
    this.checkDisabled(this.page);


    this.countTime(this.page);

    if (!this.page) {
      this.stopInactivityTimer();

      if (this.hasSubstantiveAnswer()) {
        this.onFinishPoll.emit(this.responses);
      }

      this.responses = [];
      return;
    }

    this.touchSession();
  }

  /**
   * Despeja no array a pagina que esta sendo DEIXADA. So faz sentido ao avancar:
   * quem reinicia o fluxo usa startNewSession(), que descarta o estado em vez de despejar.
   */
  private flushCurrentPage(): void {
    const current = this.page;
    if (!current) {
      return;
    }

    current.questions.forEach((q) => {
      this.upsertResponse({
        pageId: current.id,
        questionId: q.id!,
        comment: q.type === 3 ? q.value?.[0] ?? null : null,
        pollAnswerValues:
          q.type !== 3
            ? q.value?.map((v) => {
              return { valueId: v };
            }) ?? []
            : [],
        questionType: q.type as 1 | 2 | 3,
        incomplete: q.incomplete ?? false,
      });
    });
  }

  /**
   * Garante no maximo uma resposta por (pagina, questao), preservando a posicao original
   * para que a ordem do payload continue sendo a ordem de saida das paginas.
   * Laco simples de proposito: lib-ui compila com lib ES5, sem Map nem findIndex.
   */
  private upsertResponse(response: IResponses): void {
    for (let i = 0; i < this.responses.length; i++) {
      const current = this.responses[i];

      if (
        current.pageId === response.pageId &&
        current.questionId === response.questionId
      ) {
        this.responses[i] = response;
        return;
      }
    }

    this.responses.push(response);
  }

  private hasSubstantiveAnswer(): boolean {
    return this.responses.some(
      (r) => r.pollAnswerValues.length > 0 || !!r.comment,
    );
  }

  /** Inicia uma sessao de respondente: descarta todo o estado anterior e abre a pagina 1. */
  private startNewSession(): void {
    this.stopTimer();
    this.stopInactivityTimer();

    this.responses = [];
    this.pollIncomplete = false;
    this.page = undefined;
    this.nextPage = null;
    this.indexSelectedPage = 0;

    this.selectPage(0);
  }

  /** Alguem ja agiu na pagina 1: existe sessao em andamento. */
  private get hasSessionStarted(): boolean {
    return (
      this.responses.length > 0 ||
      !!this.page?.questions.some((q) => !!q.value?.length)
    );
  }

  private pageHasOwnCountdown(page?: IPage): boolean {
    return !!page && page.pageNumber !== 1 && page.timer > 0;
  }

  /**
   * Rearma o limite de inatividade. So vale depois da acao inicial na pagina 1 e apenas
   * em paginas sem timer proprio, que ja tem seu proprio encerramento.
   */
  private touchSession(): void {
    this.stopInactivityTimer();

    if (!this.hasSessionStarted || this.pageHasOwnCountdown(this.page)) {
      return;
    }

    this.inactivityTimeoutId = setTimeout(() => {
      this.handleInactivityTimeout();
    }, INACTIVITY_TIMEOUT_SECONDS * 1000);
  }

  private stopInactivityTimer(): void {
    if (this.inactivityTimeoutId) {
      clearTimeout(this.inactivityTimeoutId);
      this.inactivityTimeoutId = null;
    }
  }

  /**
   * Sessao abandonada. Satisfacao: marca como incompleta e envia o que ja foi respondido.
   * Demais tipos: abandona sem enviar.
   */
  private handleInactivityTimeout(): void {
    this.inactivityTimeoutId = null;

    if (!this.hasSessionStarted) {
      return;
    }

    if (!this.isSatisfaction) {
      this.startNewSession();
      return;
    }

    this.page?.questions.forEach((q) => {
      q.incomplete = true;
    });
    this.pollIncomplete = true;
    this.selectPage(END_OF_POLL_PAGE_INDEX);
  }

  selectPageAnswerNext(index: number): void {
    let next = this.nextPage ? this.nextPage : this.getNextPage(index);

    this.selectPage(next ? next - 1 : this.indexSelectedPage + 1);
  }

  selectUniqueAnswer(page: IPage, next: number | null = null): void {
    this.nextPage = next;
    if (!this.showBtn) {
      this.selectPage(next ? next - 1 : this.indexSelectedPage + 1);
    } else {
      this.checkDisabled(page);
      this.touchSession();
    }
  }

  addAnswerType2(question: IQuestion, id: string): void {
    if (question.value instanceof Array) {
      if (question.value) {
        if (!question.value.some((value) => value === id)) {
          question.value.push(id);
        } else question.value = question.value.filter((item) => item !== id);
      } else {
        question.value = [id];
      }
    }
  }

  createPropToAnswer(page: IPage): IPage {
    if (!page) return page;

    const pageCopy = JSON.parse(JSON.stringify(page)) as IPage;

    pageCopy.questions.forEach((q) => {
      q.value = [];
    });

    return pageCopy;
  }

  checkDisabled(page?: IPage, questionText: boolean = false): void {
    let isDisabled = false;
    page?.questions.forEach((Q) => {
      if (Q.requiredAnswer && !Q.value?.length) {
        isDisabled = true;
      }
    });

    this.disabled = isDisabled;

    if (questionText) {
      this.countTime(page);
      this.touchSession();
    }
  }

  countTime(page?: IPage): void {
    this.stopTimer();

    if (!page) {
      this.startTimer(this.poll.endPage.timer, () => {
        this.startNewSession();
        if (this.incentive) {
          if (this.drawNumber > 0) {
            this.showWinnerIncentive = true;
          } else {
            this.showInitialIncentive = true;
          }
        }
      });
      return;
    }

    if (this.pageHasOwnCountdown(page)) {
      this.startTimer(page.timer, () => {
        page.questions.forEach((q) => {
          q.incomplete = true;
        });
        this.pollIncomplete = true;
        this.selectPage(END_OF_POLL_PAGE_INDEX);
      });
    }
  }

  trackByQuestionNumber(index: number, question: IQuestion): number {
    return question.number;
  }

  trackByAnswerId(index: number, answer: any): string {
    return answer.id;
  }

  getNextPage(index: number): number | null {
    const pageSelected = this.poll.pages[index];
    let next = 0;

    pageSelected.questions.forEach((q) => {
      q.answers.forEach((a) => {
        if (a.next) {
          next = a.next;
        }
      });
    });

    return next;
  }

  async handleIncentiveAction(action: "initial" | "winner") {
    if (action === 'initial') {
      this.drawNumber = 0;
      if (!this.incentive) {
        this.showInitialIncentive = false;
        return;
      }

      if (navigator.onLine) {
        const drawValidate = await lastValueFrom(this.drawService.validateWinnerByBranchId(this.incentive?.branchId));
        if (drawValidate?.data && drawValidate.data > 0) {
          this.drawNumber = drawValidate.data;
        }
      }

      this.showInitialIncentive = false;
      this.startNewSession();
      window.scrollTo(0, 0);
    } else {
      this.showWinnerIncentive = false;
      this.showInitialIncentive = true;

      const answered = localStorage.getItem('answered');
      if (answered) {
        this.isLoading = false;
      }
    }
  }

}
